import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, Wrench, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SideMenu from '../components/SideMenu';
import AssistenciaTecnica from '../components/AssistenciaTecnica';
import ArtigoFormatacaoPC from '../components/ArtigoFormatacaoPC';
import ArtigoTrocaTelaNotes from '../components/ArtigoTrocaTelaNotes';
import AgendarDiagnosticoModal from '../components/AgendarDiagnosticoModal';
import PrivacyPolicy from '../components/PrivacyPolicy';
import CookieConsent from '../components/CookieConsent';
import StickyBreadcrumb from '../components/StickyBreadcrumb';
import { Toaster } from '../components/ui/toaster';

const PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://fernandotech.com.br/assistencia-tecnica",
      "url": "https://fernandotech.com.br/assistencia-tecnica",
      "name": "Assistência Técnica para Notebook e Computador em Ribeirão Preto | Fernando Tech",
      "description": "Assistência técnica especializada em notebook, desktop, monitor e no-break em Ribeirão Preto. Diagnóstico gratuito. Fernando Tech — desde 2008.",
      "inLanguage": "pt-BR",
      "isPartOf": { "@id": "https://fernandotech.com.br/#website" }
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fernandotech.com.br/" },
        { "@type": "ListItem", "position": 2, "name": "Assistência Técnica", "item": "https://fernandotech.com.br/assistencia-tecnica" }
      ]
    },
    {
      "@type": "Article",
      "@id": "https://fernandotech.com.br/assistencia-tecnica#artigo-formatacao",
      "headline": "Quanto Custa Formatar um Computador em 2026?",
      "description": "Guia completo com preços médios de formatação de computador em 2026, o que está incluso, quando vale a pena formatar e serviço local em Ribeirão Preto.",
      "author": { "@type": "Organization", "name": "Fernando Tech", "url": "https://fernandotech.com.br" },
      "publisher": { "@id": "https://fernandotech.com.br/#localbusiness" },
      "datePublished": "2026-01-15",
      "dateModified": "2026-01-15",
      "image": "https://fernandotech.com.br/fernando-tech-logo.png",
      "mainEntityOfPage": "https://fernandotech.com.br/assistencia-tecnica",
      "inLanguage": "pt-BR",
      "about": {
        "@type": "Service",
        "name": "Formatação de Computador",
        "provider": { "@id": "https://fernandotech.com.br/#localbusiness" },
        "areaServed": { "@type": "City", "name": "Ribeirão Preto" },
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "80",
          "highPrice": "480",
          "priceCurrency": "BRL",
          "offerCount": "4"
        }
      }
    },
    {
      "@type": "Article",
      "@id": "https://fernandotech.com.br/assistencia-tecnica#artigo-tela-notebook",
      "headline": "Quanto Custa Trocar a Tela de Notebook em 2026?",
      "description": "Guia completo com preços médios de troca de tela de notebook em 2026, quando vale a pena trocar, e serviço local em Ribeirão Preto.",
      "author": { "@type": "Organization", "name": "Fernando Tech", "url": "https://fernandotech.com.br" },
      "publisher": { "@id": "https://fernandotech.com.br/#localbusiness" },
      "datePublished": "2026-01-15",
      "dateModified": "2026-01-15",
      "image": "https://fernandotech.com.br/fernando-tech-logo.png",
      "inLanguage": "pt-BR",
      "about": {
        "@type": "Service",
        "name": "Troca de Tela de Notebook",
        "provider": { "@id": "https://fernandotech.com.br/#localbusiness" },
        "areaServed": { "@type": "City", "name": "Ribeirão Preto" },
        "offers": { "@type": "AggregateOffer", "lowPrice": "699", "highPrice": "1250", "priceCurrency": "BRL", "offerCount": "3" }
      }
    },
    {
      "@type": "FAQPage",
      "@id": "https://fernandotech.com.br/assistencia-tecnica#faq-tela-notebook",
      "mainEntity": [
        { "@type": "Question", "name": "Quanto custa trocar a tela de um notebook em Ribeirão Preto?", "acceptedAnswer": { "@type": "Answer", "text": "A peça (tela) custa entre R$ 399 e R$ 650 dependendo do modelo. A mão de obra é R$ 300 fixo. O total fica entre R$ 699 e R$ 950 para notebooks comuns (14\" e 15.6\" FHD). Telas touchscreen têm custo mais elevado. Na Fernando Tech o diagnóstico é gratuito." } },
        { "@type": "Question", "name": "Vale a pena trocar a tela do notebook ou comprar um novo?", "acceptedAnswer": { "@type": "Answer", "text": "Se o notebook tem menos de 5 anos, trocar a tela é quase sempre mais barato. Um notebook novo de entrada custa R$ 1.800+, enquanto a troca fica entre R$ 699 e R$ 950 na maioria dos casos." } },
        { "@type": "Question", "name": "Quanto tempo leva para trocar a tela do notebook?", "acceptedAnswer": { "@type": "Answer", "text": "De 1 a 3 horas para notebooks comuns. Ultrabooks podem levar de 3 a 5 horas. Na Fernando Tech entregamos no mesmo dia na maioria dos casos." } },
        { "@type": "Question", "name": "A troca de tela de notebook tem garantia?", "acceptedAnswer": { "@type": "Answer", "text": "Sim. Todos os serviços de troca de tela na Fernando Tech têm garantia de 90 dias na peça e na mão de obra." } }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": "https://fernandotech.com.br/assistencia-tecnica#faq-formatacao",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quanto custa formatar um computador em Ribeirão Preto?",
          "acceptedAnswer": { "@type": "Answer", "text": "Em Ribeirão Preto, a formatação básica custa entre R$ 80 e R$ 120. A formatação completa — com backup, drivers, programas e limpeza — fica entre R$ 150 e R$ 220. Na Fernando Tech, o diagnóstico é gratuito antes de qualquer serviço." }
        },
        {
          "@type": "Question",
          "name": "Quanto tempo leva para formatar um computador?",
          "acceptedAnswer": { "@type": "Answer", "text": "Uma formatação simples leva de 2 a 4 horas. Com backup e instalação de programas, pode levar de 4 a 8 horas. Na Fernando Tech entregamos em até 24h na maioria dos casos." }
        },
        {
          "@type": "Question",
          "name": "Meus arquivos são apagados na formatação?",
          "acceptedAnswer": { "@type": "Answer", "text": "Sim — a formatação apaga tudo. Por isso, o serviço de backup é altamente recomendado. Fazemos backup em HD externo ou nuvem antes de formatar." }
        },
        {
          "@type": "Question",
          "name": "Vale a pena formatar um computador lento?",
          "acceptedAnswer": { "@type": "Answer", "text": "Depende. Se a lentidão é causada por vírus, muito lixo acumulado ou sistema corrompido, a formatação resolve. Se o problema for hardware (pouca RAM, HD antigo), recomendamos upgrade. Nossa análise gratuita identifica a causa." }
        },
        {
          "@type": "Question",
          "name": "Qual a diferença entre formatação simples e completa?",
          "acceptedAnswer": { "@type": "Answer", "text": "A formatação simples reinstala apenas o Windows. A completa inclui backup dos seus arquivos, instalação de drivers, programas essenciais (navegador, antivírus, PDF) e limpeza interna do equipamento." }
        },
        {
          "@type": "Question",
          "name": "Quanto custa formatar e instalar o Windows em 2026?",
          "acceptedAnswer": { "@type": "Answer", "text": "A formatação com instalação do Windows custa entre R$ 80 e R$ 120 para o serviço básico, ou entre R$ 150 e R$ 220 para o pacote completo com backup e programas, em Ribeirão Preto." }
        }
      ]
    }
  ]
};

export default function AssistenciaTecnicaPage() {
  const [showAgendar, setShowAgendar]           = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  useEffect(() => {
    // Título e meta description da página
    document.title = 'Assistência Técnica em Notebook e Computador Ribeirão Preto | Fernando Tech';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Assistência técnica em Ribeirão Preto: formatação (R$ 80–R$ 220), troca de tela de notebook (R$ 200–R$ 420), diagnóstico gratuito. Fernando Tech — desde 2008, entrega em 24h.';

    // Canonical da página
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://fernandotech.com.br/assistencia-tecnica';

    // JSON-LD da página
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id   = 'ld-assistencia-tecnica';
    script.text = JSON.stringify(PAGE_SCHEMA);
    document.head.appendChild(script);

    return () => {
      document.title = 'Fernando Tech – Suporte Técnico Premium - Ribeirão Preto e Região';
      const ldScript = document.getElementById('ld-assistencia-tecnica');
      if (ldScript) ldScript.remove();
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.href = 'https://fernandotech.com.br/';
    };
  }, []);

  return (
    <div className="bg-slate-950 min-h-screen">
      <Navbar />

      {/* Breadcrumb — fixed logo abaixo da Navbar (mantém visível no scroll) */}
      <StickyBreadcrumb>
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 hover:text-cyan-400 transition-colors">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
          <span className="flex items-center gap-1 text-cyan-400">
            <Wrench className="w-3.5 h-3.5" /> Assistência Técnica
          </span>
        </nav>
      </StickyBreadcrumb>

      {/* Conteúdo principal */}
      <AssistenciaTecnica />

      {/* Artigo SEO: Quanto custa formatar um computador */}
      <section className="bg-slate-950 border-t border-slate-800/60">
        <div className="container mx-auto">
          <ArtigoFormatacaoPC onAgendar={() => setShowAgendar(true)} />
        </div>
      </section>

      {/* Separador visual entre artigos */}
      <div className="border-t-2 border-dashed border-slate-800 mx-4 sm:mx-8" />

      {/* Artigo SEO: Quanto custa trocar tela de notebook */}
      <section className="bg-slate-950 border-t border-slate-800/60">
        <div className="container mx-auto">
          <ArtigoTrocaTelaNotes onAgendar={() => setShowAgendar(true)} />
        </div>
      </section>

      {/* CTA section */}
      <section className="bg-gradient-to-r from-violet-900/40 to-cyan-900/40 border-t border-slate-800 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-white font-bold text-2xl md:text-3xl mb-3">
            Pronto para resolver o problema do seu equipamento?
          </h2>
          <p className="text-slate-400 text-base mb-8 max-w-xl mx-auto">
            Diagnóstico gratuito e orçamento sem compromisso. Atendemos em Ribeirão Preto e região.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setShowAgendar(true)}
              className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white font-bold rounded-full shadow-lg shadow-cyan-500/20 transition-all text-base"
              data-testid="cta-agendar-btn"
            >
              <Calendar className="w-5 h-5" />
              Agendar Diagnóstico Gratuito
            </button>
            <a
              href="https://wa.me/5516991378312?text=Olá!%20Preciso%20de%20assistência%20técnica."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all text-base"
              data-testid="cta-whatsapp-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
              Chamar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)} />
      <SideMenu />
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)} />
      <PrivacyPolicy isOpen={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />
      <AgendarDiagnosticoModal isOpen={showAgendar} onClose={() => setShowAgendar(false)} />
      <Toaster />

      {/* Sticky floating button */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] sm:left-auto sm:translate-x-0 sm:right-6">
        <button
          onClick={() => setShowAgendar(true)}
          className="flex items-center gap-2.5 px-5 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white font-bold rounded-full shadow-xl shadow-cyan-500/30 transition-all hover:scale-105 text-sm whitespace-nowrap"
          data-testid="floating-agendar-btn"
        >
          <Calendar className="w-4 h-4" />
          Agendar Diagnóstico
        </button>
      </div>
    </div>
  );
}
